import { Dispatch } from 'redux';
import React from 'react';
import i18n from 'util/i18n';
import * as formTypes from 'constants/formTypes';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { RelationshipsPeopleItemModel } from 'forms/sections/relationships/people/types';
import {
  CommonOnUpdate, CommonOnError, CommonRequired, CommonAddressBooks
} from 'types/props';
import { CheckboxModel, TelephoneModel } from 'forms/shared/types';
import { EmptyListInstance } from 'forms/shared/models';
import Field from 'components/Form/Field';
import DateRange from 'components/Form/DateRange';
import Show from 'components/Form/Show';
import Name from 'components/Form/Name';
import NotApplicable from 'components/Form/NotApplicable';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import Checkbox from 'components/Form/Checkbox';
import Telephone from 'components/Form/Telephone';
import Email from 'components/Form/Email';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import { isFormSF852013, isPVQForm } from 'helpers/form';
import Comments from 'components/Form/Comments';
import EmailCollection from 'components/Form/PVQEmail/EmailCollection';
import LocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import { contactFrequencyOptions, relationshipOptionsPvq } from 'constants/enums/people';
import Dropdown from 'components/Form/Dropdown';
import PVQName from 'components/Form/PVQName';
import Textarea from 'components/Form/Textarea';

interface Props extends ValidationElementProps, Partial<RelationshipsPeopleItemModel> {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  scrollIntoView?: CommonRequired
  addressBooks?: CommonAddressBooks
  addressBook?: any
  required?: CommonRequired
  formType: formTypes.FormTypes
  formVersion: string

  dispatch?: Dispatch
}

interface State {
  otherPhoneValid: boolean
  mobilePhoneValid: boolean
}

export default class Person extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      otherPhoneValid: false,
      mobilePhoneValid: false,
    };
  }

  onPhoneError = (phoneType: string) => (value, arr: ErrorCode[]) => {
    const isValid = arr.filter((err) => !err.code.includes('.extension.') && !err.valid).length === 0;
    if (phoneType === 'mobile') {
      this.setState({ mobilePhoneValid: isValid });
    } else {
      this.setState({ otherPhoneValid: isValid });
    }
    return this.props.onError(value, arr);
  };

  onPhoneNumberRequiredMsg = (isPhoneNumberValid: boolean, phoneInfo: TelephoneModel) => {
    // do not show the message if there is a valid phone#.
    if (this.state.otherPhoneValid || this.state.mobilePhoneValid) {
      return '';
    }

    if ((phoneInfo && phoneInfo.noNumber) || isPhoneNumberValid) {
      return '';
    }
    const msg = 'At least one phone number is required for this person';
    return (
      <div className="message error usa-alert usa-alert-info" role="alert" aria-live="polite">
        <div className="usa-alert-body">{msg}</div>
      </div>
    );
  };

  handleNameError = (value, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<RelationshipsPeopleItemModel>) => {
    const { onUpdate } = this.props;

    onUpdate({
      Name: this.props.Name,
      Name2013: this.props.Name2013,
      Dates: this.props.Dates,
      Rank: this.props.Rank,
      RankNotApplicable: this.props.RankNotApplicable,
      Relationship: this.props.Relationship,
      RelationshipOther: this.props.RelationshipOther,
      MobileTelephone: this.props.MobileTelephone,
      OtherTelephone: this.props.OtherTelephone,
      Email: this.props.Email,
      Emails: this.props.Emails,
      EmailNotApplicable: this.props.EmailNotApplicable,
      ContactFrequency: this.props.ContactFrequency,
      ContactFrequencyOther: this.props.ContactFrequencyOther,
      Address: this.props.Address,
      Comments: this.props.Comments,
      CurrentRelationship: this.props.CurrentRelationship,
      CurrentRelationshipOtherExplanation: this.props.CurrentRelationshipOtherExplanation,
      ...queue,
    });
  };

  updateName = (values: RelationshipsPeopleItemModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updateName2013 = (values: RelationshipsPeopleItemModel['Name2013']) => {
    this.update({
      Name2013: values,
    });
  };

  updateDates = (values: RelationshipsPeopleItemModel['Dates']) => {
    this.update({
      Dates: values,
    });
  };

  updateContactFrequency = (values: RelationshipsPeopleItemModel['ContactFrequency']) => {
    this.update({
      ContactFrequency: values,
    });
  };

  updateContactFrequencyOther = (values: RelationshipsPeopleItemModel['ContactFrequencyOther']) => {
    this.update({
      ContactFrequencyOther: values,
    });
  };

  updateRank = (values: RelationshipsPeopleItemModel['Rank']) => {
    this.update({
      Rank: values,
    });
  };

  updateRankNotApplicable = (values: RelationshipsPeopleItemModel['RankNotApplicable']) => {
    this.update({
      Rank: values.applicable ? this.props.Rank : {} as RelationshipsPeopleItemModel['Rank'],
      RankNotApplicable: values,
    });
  };

  updateRelationship = (values: CheckboxModel) => {
    const relations = values.value;
    const currentRelationships = (this.props.Relationship || {}).values || [];
    const selected = [...currentRelationships];

    if (selected.includes(relations)) {
      // Remove the relationship if it was previously selected
      selected.splice(selected.indexOf(relations), 1);
    } else {
      selected.push(relations);
    }
    const updateObj: Partial<RelationshipsPeopleItemModel> = {
      Relationship: {
        values: selected,
        name: values.name || '',
      },
    };

    if (!selected.includes('Other')) {
      updateObj.RelationshipOther = {} as RelationshipsPeopleItemModel['RelationshipOther'];
    }
    this.update(updateObj);
  };

  updateRelationshipOther = (values: RelationshipsPeopleItemModel['RelationshipOther']) => {
    this.update({
      RelationshipOther: values,
    });
  };

  updateMobileTelephone = (values: RelationshipsPeopleItemModel['MobileTelephone']) => {
    this.update({
      MobileTelephone: values,
    });
  };

  updateOtherTelephone = (values: RelationshipsPeopleItemModel['OtherTelephone']) => {
    this.update({
      OtherTelephone: values,
    });
  };

  updateEmail = (values: RelationshipsPeopleItemModel['Email']) => {
    this.update({
      Email: values,
    });
  };

  updateEmails = (values: RelationshipsPeopleItemModel['Emails']) => {
    this.update({
      Emails: values,
    });
  };

  updateEmailNotApplicable = (values: RelationshipsPeopleItemModel['EmailNotApplicable']) => {
    this.update({
      Email: {} as RelationshipsPeopleItemModel['Email'],
      EmailNotApplicable: values,
    });
  };

  updateAddress = (values: RelationshipsPeopleItemModel['Address']) => {
    this.update({
      Address: values,
    });
  };

  updateCurrentRelationship = (values: RelationshipsPeopleItemModel['CurrentRelationship']) => {
    this.update({
      CurrentRelationship: values,
    });
  };

  updateCurrentRelationshipOtherExplanation = (values: RelationshipsPeopleItemModel['CurrentRelationshipOtherExplanation']) => {
    this.update({
      CurrentRelationshipOtherExplanation: values,
    });
  };

  updateComments = (values: RelationshipsPeopleItemModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  render() {
    const { formType, formVersion } = this.props;
    const contactOptions = contactFrequencyOptions.map((s) => (
      <option key={s} value={s}>
        {s}
      </option>
    ));
    const attribute = `List.items[${this.props.$index}].Item`;
    const pvqPrefix = 'relationships.pvq.people';

    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div className="person">
        <Field
          title={i18n.t('relationships.pvq.people.person.heading.name')}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Name`}
        >
          <PVQName
            name="Name"
            className="name"
            aria-label="Provide this person's full name"
            {...this.props.Name}
            onUpdate={this.updateName}
            onError={this.handleNameError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            showMiddleUnknown
            attribute={`${attribute}.Name`}
            showLastFirstMiddleNameOrder
          />
        </Field>

        <Field
          title="Dates Known"
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="daterange"
          attribute={`${attribute}.Dates`}
        >
          <DateRange
            name="Dates"
            className="known-dates"
            minDateEqualTo
            {...this.props.Dates}
            onUpdate={this.updateDates}
            onError={this.props.onError}
            required={this.props.required}
            allowPresent={false}
            hideFromDay
            hideToDay
            attribute={`${attribute}.Dates`}
            usePvqFromDate
            usePvqToDate
            fromDateLabel={i18n.t('relationships.pvq.people.person.heading.fromDate')}
            toDateLabel={i18n.t('relationships.pvq.people.person.heading.toDate')}
          />
        </Field>
        <Field
          title={i18n.t('relationships.pvq.people.person.heading.contactFrequency')}
          scrollIntoView={this.props.scrollIntoView}
          attribute="ContactFrequency"
        >
          <Dropdown
            placeholder={this.props.ContactFrequency?.value}
            attribute={`${attribute}.ContactFrequency`}
            name="ContactFrequency"
            {...this.props.ContactFrequency}
            onUpdate={this.updateContactFrequency}
            onError={this.props.onError}
            sort={false}
            value={this.props.ContactFrequency?.value}
            required={this.props.required}
            suggestionsRenderProvided
          >
            {contactOptions}
          </Dropdown>
        </Field>

        {this.props.ContactFrequency?.value === 'Other' && (
          <Field
            title={i18n.t('relationships.pvq.people.person.heading.contactFrequencyOther')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ContactFrequencyOther`}
          >
            <Text
              name="ContactFrequencyOther"
              maxlength="100"
              className="contact-frequency-other"
              {...this.props.ContactFrequencyOther}
              onUpdate={this.updateContactFrequencyOther}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.ContactFrequencyOther`}
            />
          </Field>
        )}
        <Field
          title={i18n.t('relationships.pvq.people.person.heading.relationship')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CurrentRelationship`}
        >
          <Dropdown
            name="current-relationship"
            {...this.props.CurrentRelationship}
            onUpdate={this.updateCurrentRelationship}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.CurrentRelationship`}
            value={this.props.CurrentRelationship?.value}
          >
            {relationshipOptionsPvq.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Dropdown>

        </Field>
        {this.props.CurrentRelationship?.value === 'Other'

          && (
          <Field
            title={i18n.t('relationships.pvq.people.person.heading.contactFrequencyOther')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.CurrentRelationshipOtherExplanation`}
          >

            <Textarea
              name="CurrentRelationshipOtherExplanation"
              className="current-relationship-other"
              {...this.props.CurrentRelationshipOtherExplanation}
              onUpdate={this.updateCurrentRelationshipOtherExplanation}
              attribute={`${attribute}.CurrentRelationshipOtherExplanation`}
            />

          </Field>
          )}
        <Field
          title={i18n.t('relationships.pvq.people.person.heading.telephone')}
          className="mobile-telephone override-required"
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="telephone"
          attribute={`${attribute}.MobileTelephone`}
        >
          {this.onPhoneNumberRequiredMsg(this.state.mobilePhoneValid, this.props.MobileTelephone)}
          <Telephone
            name="MobileTelephone"
            {...this.props.MobileTelephone}
            showTimeOfDay
            showNumberType
            onUpdate={this.updateMobileTelephone}
            onError={this.onPhoneError('mobile')}
            required={this.props.required}
            allowNotApplicable={false}
            attribute={`${attribute}.MobileTelephone`}
          />
        </Field>
        <EmailCollection
          onUpdate={this.updateEmails}
          onError={this.props.onError}
          required={this.props.required}
          attribute={`${attribute}.Emails`}
          emailFieldTitle={i18n.t('relationships.pvq.civilUnion.divorce.heading.email')}
          appendFieldTitle={i18n.t('relationships.pvq.people.person.emails.append')}
          branchFieldTitle={i18n.t('relationships.pvq.people.person.emails.branch.title')}
          includeEmailType={false}
          hasSecondaryAppendLabels
          secondaryNoAppendLabel="No"
          secondaryYesAppendLabel="Add"

          showEmailUnknown
          {...this.props.Emails}
        />
        <LocationUnknown
          {...this.props.Address}
          attribute={`${attribute}.Address`}
          hideInstallationNameUnknownExplanation
          name="Address"
          label={i18n.t('relationships.pvq.people.person.heading.address')}
          domesticTitle={i18n.t('relationships.pvq.people.person.heading.domesticAddress')}
          foreignTitle={i18n.t('relationships.pvq.people.person.heading.foreignAddress')}
          includeMilitaryStates
          layout={LocationUnknown.ADDRESS}
          geocode
          addressBook={this.props.addressBook}
          addressBooks={this.props.addressBooks}
          dispatch={this.props.dispatch}
          onUpdate={this.updateAddress}
          onError={this.props.onError}
          required={this.props.required}
          isPoBoxAllowed={false}
          showMilitaryInstallation
          hideInstallationNameUnknownWhenForeign
          showIsInstallationUnknownIfInternational
          showIsInstallationUnknownIfDomestic
          showApoFpoDpoPostcodeWhenForeign
          showInternationalStreet
          internationalStreetLabel={i18n.t('address.pvq.international.street.label')}
        />

      </div>
    ) : (
      <div className="person">
        <Field
          title={i18n.t('relationships.people.person.heading.knownDates')}
          help="relationships.people.person.help.knownDates"
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="daterange"
          attribute={`${attribute}.Dates`}
        >
          <DateRange
            name="Dates"
            className="known-dates"
            minDateEqualTo
            {...this.props.Dates}
            onUpdate={this.updateDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
            attribute={`${attribute}.Dates`}
          />
        </Field>
        <Show when={formType !== formTypes.SF852013}>
          <Field
            title={i18n.t('relationships.people.person.heading.name')}
            optional
            filterErrors={Name.requiredErrorsOnly}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Name`}
          >
            <Name
              name="Name"
              className="name"
              aria-label="Provide this person's full name"
              {...this.props.Name}
              onUpdate={this.updateName}
              onError={this.handleNameError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Name`}
            />
          </Field>
        </Show>
        <Show when={formType === formTypes.SF852013}>
          <Field
            title={i18n.t('relationships.people.person.heading.name')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Name2013`}
          >
            <Text
              name="Name"
              className="name"
              {...this.props.Name2013}
              onUpdate={this.updateName2013}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Name2013`}
            />
          </Field>
          <Field
            title={i18n.t('relationships.people.person.heading.otherTelephoneSF852013')}
            className="mobile-telephone override-required"
            scrollIntoView={this.props.scrollIntoView}
            adjustFor="telephone"
            attribute={`${attribute}.OtherTelephone`}
          >
            <Telephone
              name="OtherTelephone"
              {...this.props.OtherTelephone}
              showTimeOfDay
              onUpdate={this.updateOtherTelephone}
              onError={this.onPhoneError('other')}
              required={this.props.required}
              attribute={`${attribute}.OtherTelephone`}
              allowNotApplicable={false}
            />
          </Field>
        </Show>
        <Show when={formType !== formTypes.SF852013}>
          <Field
            title={i18n.t('relationships.people.person.heading.rank')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Rank`}
          >
            <NotApplicable
              name="RankNotApplicable"
              className="rank-notapplicable"
              {...this.props.RankNotApplicable}
              label={i18n.t('relationships.people.person.label.idk')}
              or={i18n.m('relationships.people.person.label.or')}
              onError={this.props.onError}
              onUpdate={this.updateRankNotApplicable}
              attribute={`${attribute}.RankNotApplicable`}
            >
              <Text
                name="Rank"
                className="rank"
                {...this.props.Rank}
                onUpdate={this.updateRank}
                onError={this.props.onError}
                required={(this.props.RankNotApplicable || {}).applicable && this.props.required}
                attribute={`${attribute}.Rank`}
              />
            </NotApplicable>
          </Field>

          <Field
            title={i18n.t('relationships.people.person.heading.relationship')}
            className="relationships"
            scrollIntoView={this.props.scrollIntoView}
            adjustFor="labels"
            attribute={`${attribute}.Relationship`}
          >
            {/* eslint jsx-a11y/label-has-associated-control: 0 */}
            {/* eslint jsx-a11y/label-has-for: 0 */}
            <label>{i18n.t('relationships.people.person.label.relationship.title')}</label>
            <CheckboxGroup
              className="relationship option-list eapp-extend-labels option-list-vertical"
              required={this.props.required}
              onError={this.props.onError}
              selectedValues={this.props.Relationship?.values}
              attribute={`${attribute}.Relationship`}
            >
              <Checkbox
                name="relationship-neighbor"
                label={i18n.t('relationships.people.person.label.relationship.neighbor')}
                value="Neighbor"
                onError={this.props.onError}
                onUpdate={this.updateRelationship}
              />
              <Checkbox
                name="relationship-friend"
                label={i18n.t('relationships.people.person.label.relationship.friend')}
                value="Friend"
                onError={this.props.onError}
                onUpdate={this.updateRelationship}
              />
              <Checkbox
                name="relationship-landlord"
                label={i18n.t('relationships.people.person.label.relationship.workAssociate')}
                value="WorkAssociate"
                onError={this.props.onError}
                onUpdate={this.updateRelationship}
              />
              <Checkbox
                name="relationship-business"
                label={i18n.t('relationships.people.person.label.relationship.schoolmate')}
                value="Schoolmate"
                onError={this.props.onError}
                onUpdate={this.updateRelationship}
              />
              <Checkbox
                name="relationship-other"
                label={i18n.t('relationships.people.person.label.relationship.other')}
                value="Other"
                onError={this.props.onError}
                onUpdate={this.updateRelationship}
              />
            </CheckboxGroup>

            <Show when={((this.props.Relationship || {}).values || []).includes('Other')}>
              <Field
                title={i18n.t('relationships.people.person.label.relationship.explanation')}
                titleSize="label"
                adjustFor="text"
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.RelationshipOther`}
              >
                <Text
                  name="RelationshipOther"
                  maxlength="100"
                  className="relationship-other"
                  {...this.props.RelationshipOther}
                  onUpdate={this.updateRelationshipOther}
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.RelationshipOther`}
                />
              </Field>
            </Show>
          </Field>
          <Field
            title={i18n.t('relationships.people.person.heading.mobileTelephone')}
            className="mobile-telephone override-required"
            scrollIntoView={this.props.scrollIntoView}
            adjustFor="telephone"
            attribute={`${attribute}.MobileTelephone`}
          >
            {this.onPhoneNumberRequiredMsg(this.state.mobilePhoneValid, this.props.MobileTelephone)}
            <Telephone
              name="MobileTelephone"
              {...this.props.MobileTelephone}
              showTimeOfDay={![formTypes.SF85P, formTypes.SF85PS].includes(formType)}
              onUpdate={this.updateMobileTelephone}
              onError={this.onPhoneError('mobile')}
              required={this.props.required}
              allowNotApplicable={this.props.OtherTelephone && !this.props.OtherTelephone.noNumber}
              attribute={`${attribute}.MobileTelephone`}
            />
          </Field>
        </Show>
        <Show when={formType !== formTypes.SF852013}>
          <Field
            title={i18n.t('relationships.people.person.heading.otherTelephone')}
            className="other-telephone override-required"
            scrollIntoView={this.props.scrollIntoView}
            adjustFor="telephone"
            attribute={`${attribute}.OtherTelephone`}
          >
            {this.onPhoneNumberRequiredMsg(this.state.otherPhoneValid, this.props.OtherTelephone)}
            <Telephone
              name="OtherTelephone"
              {...this.props.OtherTelephone}
              onUpdate={this.updateOtherTelephone}
              onError={this.onPhoneError('other')}
              required={this.props.required}
              allowNotApplicable={
                this.props.MobileTelephone && !this.props.MobileTelephone.noNumber
              }
              attribute={`${attribute}.OtherTelephone`}
            />
          </Field>

          <Field
            title={i18n.t('relationships.people.person.heading.email')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Email`}
          >
            <NotApplicable
              name="EmailNotApplicable"
              className="email-notapplicable"
              {...this.props.EmailNotApplicable}
              label={i18n.t('relationships.people.person.label.idk')}
              or={i18n.m('relationships.people.person.label.or')}
              onError={this.props.onError}
              onUpdate={this.updateEmailNotApplicable}
              attribute={`${attribute}.EmailNotApplicable`}
            >
              <Email
                name="Email"
                className="email"
                {...this.props.Email}
                onUpdate={this.updateEmail}
                onError={this.props.onError}
                required={(this.props.EmailNotApplicable || {}).applicable && this.props.required}
                attribute={`${attribute}.Email`}
              />
            </NotApplicable>
          </Field>
        </Show>
        <Field
          title={i18n.t('relationships.people.person.heading.address')}
          optional
          help="relationships.people.person.help.address"
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="address"
          attribute={`${attribute}.Address`}
        >
          <Location
            name="Address"
            label={i18n.t('relationships.people.person.label.address')}
            className="address"
            {...this.props.Address}
            addressBooks={this.props.addressBooks}
            addressBook={this.props.addressBook}
            dispatch={this.props.dispatch}
            layout={Location.ADDRESS}
            geocode
            showPostOffice={false}
            onUpdate={this.updateAddress}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Address`}
          />
        </Field>
        {!isFormSF852013(formType, formVersion) && (
          <Comments
            title={i18n.t('relationships.comments.optional')}
            name={`${attribute}Comments`}
            className="optional-comment"
            attribute={`${attribute}Comments`}
            {...this.props.Comments}
            onUpdate={this.updateComments}
            required={false}
          />
        )}
      </div>
    );
  }
}

Person.defaultProps = {
  Relationship: EmptyListInstance,
  RankNotApplicable: { applicable: true },
  EmailNotApplicable: { applicable: true },
  Emails: {},
  ContactFrequency: {},
  ContactFrequencyOther: {},
  Comments: {},
  CurrentRelationship: {}, // pvq only
  CurrentRelationshipOtherExplanation: {}, // pvq only
  addressBooks: {},
  addressBook: 'Reference',
  dispatch: () => { },
  onUpdate: () => { },
  onError: (value, arr) => arr,
};
